import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import testMetricsService from '../api/testMetricsService';
import type {
    TestMetric,
    CreateTestMetricDto,
    UpdateTestMetricDto,
} from '../types/types';

export const useTestMetrics = () => {
    return useQuery<TestMetric[]>({
        queryKey: ['testMetrics'],
        queryFn: () => testMetricsService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useTestMetric = (id: number | undefined) => {
    return useQuery<TestMetric>({
        queryKey: ['testMetric', id],
        queryFn: () => testMetricsService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateTestMetric = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateTestMetricDto) => testMetricsService.create(dto),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testMetrics'] });
            qc.invalidateQueries({ queryKey: ['disclaimers'] });
        },
    });
};

export const useUpdateTestMetric = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateTestMetricDto }) =>
            testMetricsService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['testMetrics'] });
            qc.invalidateQueries({ queryKey: ['testMetric', id] });
            qc.invalidateQueries({ queryKey: ['disclaimers'] });
        },
    });
};

export const useDeleteTestMetric = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => testMetricsService.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testMetrics'] });
            qc.invalidateQueries({ queryKey: ['disclaimers'] });
        },
    });
};
